import { create, show, useModal } from "@ebay/nice-modal-react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import usePersonalApiKeysState from "components/ApiKeys/PersonalApiKeys/usePersonalApiKeysState";
import FormFieldTextConfirm from "components/FormFields/Confirm";
import Box from "ds/components/Box";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import DeleteConfirmationModal from "ds/components/DeleteConfirmationModal";
import Tile from "ds/components/Tile";

type PersonalApiKeysToggleDisableModalProps = {
  keysCount: number;
};

const PersonalApiKeysToggleDisableModal = create(function PersonalApiKeysToggleDisableModal({
  keysCount,
}: PersonalApiKeysToggleDisableModalProps) {
  const modal = useModal();
  const { setEnabled, isSaving } = usePersonalApiKeysState();

  const [deactivateExisting, setDeactivateExisting] = useState(false);

  const form = useForm<{ disableConfirmationText: string }>({
    defaultValues: { disableConfirmationText: "" },
    mode: "onChange",
  });

  const handleConfirm = async () => {
    const isSuccess = await setEnabled(false, deactivateExisting);

    if (isSuccess) {
      modal.resolve();
      modal.hide();
    }
  };

  return (
    <DeleteConfirmationModal
      title="Disable Personal API keys"
      onConfirm={handleConfirm}
      isLoading={isSaving}
      isDismissable={!isSaving}
      confirmationButtonLabel="Disable"
      isConfirmationDisabled={isSaving || !form.formState.isValid}
    >
      <Box direction="column" gap="large" fullWidth padding="0 0 large 0">
        <ConfirmationModalMessage>
          No one will be able to create new personal API keys. Choose what happens to the{" "}
          {keysCount > 0 ? <strong>{keysCount} keys</strong> : "keys"} that already exist.
        </ConfirmationModalMessage>

        <Box gap="medium" fullWidth>
          <Tile
            indicator="radio"
            title="Keep existing keys"
            description="They keep working, and owners can still delete them. No one can create new ones."
            selected={!deactivateExisting}
            onClick={() => setDeactivateExisting(false)}
            disabled={isSaving}
          />
          <Tile
            indicator="radio"
            title="Deactivate existing keys"
            description="All keys stop working immediately. Any CLI, Terraform or API call using one starts failing."
            selected={deactivateExisting}
            onClick={() => setDeactivateExisting(true)}
            disabled={isSaving}
          />
        </Box>
      </Box>

      <FormProvider {...form}>
        <FormFieldTextConfirm
          fieldName="disableConfirmationText"
          textToConfirm="disable"
          disabled={isSaving}
        />
      </FormProvider>
    </DeleteConfirmationModal>
  );
});

export const showPersonalApiKeysToggleDisableModal = (
  props: PersonalApiKeysToggleDisableModalProps
) => show(PersonalApiKeysToggleDisableModal, props);
